import React, { useEffect, useState } from "react";
import Spinner from "../components/Spinner";
import TableWrapper from "../components/TableWrapper";

const FormBadge = ({ v }) => {
  const s = String(v).toUpperCase();
  const base =
    "inline-flex items-center justify-center w-6 h-5 text-xs font-semibold rounded";
  const classes =
    s === "W"
      ? `${base} bg-emerald-600/90 text-white`
      : s === "D"
        ? `${base} bg-gray-500/90 text-white`
        : s === "L"
          ? `${base} bg-red-600/90 text-white`
          : `${base} bg-gray-600/90 text-white`;
  return <span className={classes}>{s}</span>;
};

export default function MatchH2H({ matchId, homeTeamName, awayTeamName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!matchId) return;
    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/h2h/get/${matchId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              key: process.env.REACT_APP_ACCESKEY,
            },
          },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (mounted) setData(json);
      } catch (err) {
        console.error(err);
        if (mounted) setError("Failed to load H2H.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [matchId]);

  if (loading) return <Spinner text="Loading H2H..." />;
  if (error)
    return (
      <div className="p-4 bg-red-950/30 text-red-400 rounded-lg border border-red-900/50 text-sm">
        {error}
      </div>
    );
  if (!data)
    return (
      <div className="text-gray-500 p-4 text-sm">No H2H data available.</div>
    );

  const { home = {}, away = {}, h2h = [] } = data;

  const refHomeTeamName = home.team_name ?? home.name ?? homeTeamName ?? null;
  const refAwayTeamName = away.team_name ?? away.name ?? awayTeamName ?? null;

  const finishedStatusRx = /ft|aet|pen|finished|full-?time/i;
  const inProgressRx = /1h|2h|ht|live|in progress/i;
  const scheduledRx = /ns|scheduled|upcoming|tbd/i;

  const playedH2H = (h2h || []).filter((m) => {
    const timestamp = Date.parse(m.date);
    const isFuture = !isNaN(timestamp) ? timestamp > Date.now() : false;

    const status = String(m.status ?? "").toLowerCase();
    const isInProgress = inProgressRx.test(status);
    const isScheduled = scheduledRx.test(status);

    const hasScore =
      m.home_goals !== null &&
      m.home_goals !== undefined &&
      m.away_goals !== null &&
      m.away_goals !== undefined &&
      String(m.home_goals) !== "" &&
      String(m.away_goals) !== "";

    const isFinishedByStatus = finishedStatusRx.test(status);

    return (
      !isFuture &&
      !isInProgress &&
      !isScheduled &&
      (isFinishedByStatus || hasScore)
    );
  });

  const visibleH2H = showAll ? playedH2H : playedH2H.slice(0, 5);

  const computeResultForRef = (m, refTeam) => {
    const hg = Number(m.home_goals);
    const ag = Number(m.away_goals);

    if (!isNaN(hg) && !isNaN(ag)) {
      if (hg === ag) return "DRAW";

      const homeWon = hg > ag;

      if (refTeam) {
        if (String(refTeam).trim() === (m.home_name ?? m.home)?.trim())
          return homeWon ? "WIN" : "LOSS";
        if (String(refTeam).trim() === (m.away_name ?? m.away)?.trim())
          return homeWon ? "LOSS" : "WIN";
      }
      return homeWon ? "HOME WIN" : "AWAY WIN";
    }

    const r = String(m.result ?? m.outcome ?? "").toLowerCase();
    if (/win/i.test(r)) return "WIN";
    if (/loss|lose/i.test(r)) return "LOSS";
    if (/draw|d/i.test(r)) return "DRAW";
    return "-";
  };

  const resultToSymbol = (m, refTeam) => {
    const r = computeResultForRef(m, refTeam);
    if (r === "WIN") return "W";
    if (r === "LOSS") return "L";
    if (r === "DRAW") return "D";
    return "-";
  };

  const getFinalScoreColor = (m, refTeam) => {
    const result = computeResultForRef(m, refTeam);
    if (result === "WIN") return "text-green-500";
    if (result === "LOSS") return "text-red-500";
    if (result === "DRAW") return "text-gray-400";
    return "text-gray-400";
  };

  const getHalftimeScoreColor = (m, refTeam, isHomeTeam) => {
    if (!refTeam) return "text-gray-400";

    const homeHt = m.home_goals_ht;
    const awayHt = m.away_goals_ht;

    if (homeHt === undefined || awayHt === undefined) return "text-gray-400";

    if (isHomeTeam) {
      if (homeHt > awayHt) return "text-green-500";
      if (homeHt < awayHt) return "text-red-500";
      return "text-gray-400";
    } else {
      if (awayHt > homeHt) return "text-green-500";
      if (awayHt < homeHt) return "text-red-500";
      return "text-gray-400";
    }
  };

  const displayScore = (m, refTeam, isHomeTeam) => {
    if (m.home_goals !== undefined && m.away_goals !== undefined) {
      return `${m.home_goals} - ${m.away_goals}`;
    }

    if (m.goals_for !== undefined && m.goals_against !== undefined) {
      if (isHomeTeam) {
        return `${m.goals_for} - ${m.goals_against}`;
      } else {
        return `${m.goals_against} - ${m.goals_for}`;
      }
    }

    return "-";
  };

  const displayHalftimeScore = (m) => {
    if (m.home_goals_ht !== undefined && m.away_goals_ht !== undefined) {
      return `${m.home_goals_ht} - ${m.away_goals_ht}`;
    }
    return "-";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
        .replace(/\//g, ".");
    } catch {
      return "-";
    }
  };

  const tableHeaderClass =
    "text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider";
  const tableCellClass = "py-3 px-4 text-sm text-gray-400";

  const renderMobileMatchCard = (matches, teamName, isHome = true) => {
    return (
      <div className="space-y-3">
        {(matches || []).map((m, index) => {
          const isHomeTeam = m.is_home === 1;
          const opponent = m.opponent_name ?? "-";
          const score = displayScore(m, teamName, isHomeTeam);
          const htScore = displayHalftimeScore(m);
          const result = resultToSymbol(m, teamName);
          const matchDate = formatDate(m.date);
          const league = m.league_name ?? "-";

          const finalScoreColor = getFinalScoreColor(m, teamName);
          const htColor = getHalftimeScoreColor(m, teamName, isHomeTeam);

          const topTeam = isHomeTeam ? teamName : opponent;
          const bottomTeam = isHomeTeam ? opponent : teamName;

          const homeHt = htScore.split(" - ")[0];
          const awayHt = htScore.split(" - ")[1];
          const homeScore = score.split(" - ")[0];
          const awayScore = score.split(" - ")[1];

          return (
            <div
              key={m.fixture_id ?? index}
              className="bg-gray-800/50 rounded-lg p-3 border border-gray-700/50"
            >
              {/* Header cu dată, ligă centrată și W/D/L */}
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-2 flex-1">
                  <span className="text-xs text-gray-500">{matchDate}</span>
                  <span className="text-xs text-gray-500 truncate flex-1 text-center">
                    {league}
                  </span>
                </div>
                <FormBadge v={result} />
              </div>

              {/* Echipa de sus */}
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-white truncate flex-1">
                  {topTeam}
                </span>
                <div className="flex gap-4">
                  <span
                    className={`text-sm font-bold w-8 text-center ${htColor}`}
                  >
                    {isHomeTeam ? homeHt : awayHt}
                  </span>
                  <span
                    className={`text-sm font-bold w-8 text-center ${finalScoreColor}`}
                  >
                    {isHomeTeam ? homeScore : awayScore}
                  </span>
                </div>
              </div>

              {/* Echipa de jos */}
              <div className="flex justify-between items-center mt-2">
                <span className="text-sm font-medium text-white truncate flex-1">
                  {bottomTeam}
                </span>
                <div className="flex gap-4">
                  <span
                    className={`text-sm font-bold w-8 text-center ${htColor}`}
                  >
                    {isHomeTeam ? awayHt : homeHt}
                  </span>
                  <span
                    className={`text-sm font-bold w-8 text-center ${finalScoreColor}`}
                  >
                    {isHomeTeam ? awayScore : homeScore}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderMobileH2HCard = () => {
    if (playedH2H.length === 0) {
      return (
        <div className="text-gray-500 p-4 text-center bg-gray-800/30 rounded-lg border border-gray-700/50">
          No past head-to-head matches available.
        </div>
      );
    }

    return (
      <>
        <div className="space-y-3">
          {visibleH2H.map((m, index) => (
            <div
              key={m.fixture_id ?? index}
              className="bg-gray-800/50 rounded-lg p-3 border border-gray-700/50"
            >
              {/* Header cu dată, ligă centrată */}
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-2 flex-1">
                  <span className="text-xs text-gray-500">
                    {formatDate(m.date)}
                  </span>
                  <span className="text-xs text-gray-500 truncate flex-1 text-center">
                    {m.league_name ?? "-"}
                  </span>
                </div>
              </div>

              {/* Echipa gazdă */}
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-white truncate flex-1">
                  {m.home_name}
                </span>
                <div className="flex gap-4">
                  <span className="text-sm font-bold w-8 text-center text-gray-400">
                    {m.home_goals_ht ?? "-"}
                  </span>
                  <span className="text-sm font-bold w-8 text-center text-gray-400">
                    {m.home_goals ?? "-"}
                  </span>
                </div>
              </div>

              {/* Echipa oaspete */}
              <div className="flex justify-between items-center mt-2">
                <span className="text-sm font-medium text-white truncate flex-1">
                  {m.away_name}
                </span>
                <div className="flex gap-4">
                  <span className="text-sm font-bold w-8 text-center text-gray-400">
                    {m.away_goals_ht ?? "-"}
                  </span>
                  <span className="text-sm font-bold w-8 text-center text-gray-400">
                    {m.away_goals ?? "-"}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {playedH2H.length > 5 && (
          <div className="flex justify-center mt-3">
            <button
              onClick={() => setShowAll((s) => !s)}
              className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-400 rounded-lg text-xs border border-gray-700 transition-colors"
            >
              {showAll ? "Show less" : `Show ${playedH2H.length - 5} more`}
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="space-y-6">
      {/* Desktop View */}
      <div className="hidden md:block space-y-6">
        <h3 className="text-lg font-semibold text-gray-400">
          Head to Head Insights
        </h3>

        {/* HOME LAST MATCHES - Desktop */}
        <TableWrapper
          title={
            homeTeamName
              ? `${homeTeamName} — Last 10 Matches`
              : "Home Team — Last 10 Matches"
          }
          titleColor="text-green-400"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className={tableHeaderClass}>Date</th>
                  <th className={tableHeaderClass}>League</th>
                  <th className={tableHeaderClass}>Match</th>
                  <th className={tableHeaderClass}>H/A</th>
                  <th className={`${tableHeaderClass} text-center`}>HT</th>
                  <th className={`${tableHeaderClass} text-center`}>Result</th>
                  <th className={tableHeaderClass}>Res</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {(home.lastMatches || []).slice(0, 10).map((m, index) => {
                  const isHomeTeam = m.is_home === 1;
                  const opponent = m.opponent_name ?? "-";
                  const score = displayScore(m, refHomeTeamName, isHomeTeam);
                  const htScore = displayHalftimeScore(m);
                  const result = resultToSymbol(m, refHomeTeamName);
                  const matchDate = formatDate(m.date);
                  const league = m.league_name ?? "-";

                  const finalScoreColor = getFinalScoreColor(
                    m,
                    refHomeTeamName,
                  );
                  const htColor = getHalftimeScoreColor(
                    m,
                    refHomeTeamName,
                    isHomeTeam,
                  );

                  const topTeam = isHomeTeam ? refHomeTeamName : opponent;
                  const bottomTeam = isHomeTeam ? opponent : refHomeTeamName;

                  const homeHt = htScore.split(" - ")[0];
                  const awayHt = htScore.split(" - ")[1];
                  const homeScore = score.split(" - ")[0];
                  const awayScore = score.split(" - ")[1];

                  return (
                    <tr
                      key={m.fixture_id ?? index}
                      className="hover:bg-gray-800/30 transition-colors"
                    >
                      <td className="py-3 px-4 text-sm text-gray-400 whitespace-nowrap">
                        {matchDate}
                      </td>
                      <td
                        className={`${tableCellClass} truncate max-w-[150px]`}
                      >
                        {league}
                      </td>
                      <td className={tableCellClass}>
                        <div className="space-y-1">
                          <div className="text-white text-sm">{topTeam}</div>
                          <div className="text-white text-sm">{bottomTeam}</div>
                        </div>
                      </td>
                      <td className={tableCellClass}>
                        <span
                          className={`text-xs px-2 py-1 rounded ${
                            isHomeTeam
                              ? "bg-green-900/30 text-green-500"
                              : "bg-blue-900/30 text-blue-500"
                          }`}
                        >
                          {isHomeTeam ? "H" : "A"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-center">
                        <div className="space-y-1">
                          <div className={htColor}>{homeHt}</div>
                          <div className={htColor}>{awayHt}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-center">
                        <div className="space-y-1">
                          <div className={finalScoreColor}>{homeScore}</div>
                          <div className={finalScoreColor}>{awayScore}</div>
                        </div>
                      </td>
                      <td className={tableCellClass}>
                        <FormBadge v={result} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TableWrapper>

        {/* AWAY LAST MATCHES - Desktop */}
        <TableWrapper
          title={
            awayTeamName
              ? `${awayTeamName} — Last 10 Matches`
              : "Away Team — Last 10 Matches"
          }
          titleColor="text-green-400"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className={tableHeaderClass}>Date</th>
                  <th className={tableHeaderClass}>League</th>
                  <th className={tableHeaderClass}>Match</th>
                  <th className={tableHeaderClass}>H/A</th>
                  <th className={`${tableHeaderClass} text-center`}>HT</th>
                  <th className={`${tableHeaderClass} text-center`}>Result</th>
                  <th className={tableHeaderClass}>Res</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {(away.lastMatches || []).slice(0, 10).map((m, index) => {
                  const isHomeTeam = m.is_home === 1;
                  const opponent = m.opponent_name ?? "-";
                  const score = displayScore(m, refAwayTeamName, isHomeTeam);
                  const htScore = displayHalftimeScore(m);
                  const result = resultToSymbol(m, refAwayTeamName);
                  const matchDate = formatDate(m.date);
                  const league = m.league_name ?? "-";

                  const finalScoreColor = getFinalScoreColor(
                    m,
                    refAwayTeamName,
                  );
                  const htColor = getHalftimeScoreColor(
                    m,
                    refAwayTeamName,
                    isHomeTeam,
                  );

                  const topTeam = isHomeTeam ? refAwayTeamName : opponent;
                  const bottomTeam = isHomeTeam ? opponent : refAwayTeamName;

                  const homeHt = htScore.split(" - ")[0];
                  const awayHt = htScore.split(" - ")[1];
                  const homeScore = score.split(" - ")[0];
                  const awayScore = score.split(" - ")[1];

                  return (
                    <tr
                      key={m.fixture_id ?? index}
                      className="hover:bg-gray-800/30 transition-colors"
                    >
                      <td className="py-3 px-4 text-sm text-gray-400 whitespace-nowrap">
                        {matchDate}
                      </td>
                      <td
                        className={`${tableCellClass} truncate max-w-[150px]`}
                      >
                        {league}
                      </td>
                      <td className={tableCellClass}>
                        <div className="space-y-1">
                          <div className="text-white text-sm">{topTeam}</div>
                          <div className="text-white text-sm">{bottomTeam}</div>
                        </div>
                      </td>
                      <td className={tableCellClass}>
                        <span
                          className={`text-xs px-2 py-1 rounded ${
                            isHomeTeam
                              ? "bg-green-900/30 text-green-500"
                              : "bg-blue-900/30 text-blue-500"
                          }`}
                        >
                          {isHomeTeam ? "H" : "A"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-center">
                        <div className="space-y-1">
                          <div className={htColor}>{homeHt}</div>
                          <div className={htColor}>{awayHt}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-center">
                        <div className="space-y-1">
                          <div className={finalScoreColor}>{homeScore}</div>
                          <div className={finalScoreColor}>{awayScore}</div>
                        </div>
                      </td>
                      <td className={tableCellClass}>
                        <FormBadge v={result} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TableWrapper>

        {/* HEAD TO HEAD - Desktop */}
        <TableWrapper titleColor="text-green-400" title="Head to Head History">
          {playedH2H.length === 0 ? (
            <div className="text-gray-500 p-6 text-center bg-gray-800/30 rounded-lg border border-gray-700/50">
              No past head-to-head matches available.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className={tableHeaderClass}>Date</th>
                      <th className={tableHeaderClass}>League</th>
                      <th className={tableHeaderClass}>Match</th>
                      <th className={`${tableHeaderClass} text-center`}>HT</th>
                      <th className={`${tableHeaderClass} text-center`}>
                        Result
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {visibleH2H.map((m, index) => (
                      <tr
                        key={m.fixture_id ?? index}
                        className="hover:bg-gray-800/30 transition-colors"
                      >
                        <td className="py-3 px-4 text-sm text-gray-400 whitespace-nowrap">
                          {formatDate(m.date)}
                        </td>
                        <td
                          className={`${tableCellClass} truncate max-w-[150px]`}
                        >
                          {m.league_name ?? "-"}
                        </td>
                        <td className={tableCellClass}>
                          <div className="space-y-1">
                            <div className="text-white text-sm">
                              {m.home_name}
                            </div>
                            <div className="text-white text-sm">
                              {m.away_name}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-center">
                          <div className="space-y-1">
                            <div className="text-gray-400">
                              {m.home_goals_ht ?? "-"}
                            </div>
                            <div className="text-gray-400">
                              {m.away_goals_ht ?? "-"}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-center">
                          <div className="space-y-1">
                            <div className="text-gray-400">
                              {m.home_goals ?? "-"}
                            </div>
                            <div className="text-gray-400">
                              {m.away_goals ?? "-"}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {playedH2H.length > 5 && (
                <div className="flex justify-center mt-3">
                  <button
                    onClick={() => setShowAll((s) => !s)}
                    className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-400 rounded-lg text-sm border border-gray-700 transition-colors"
                  >
                    {showAll
                      ? "Show less"
                      : `Show ${playedH2H.length - 5} more`}
                  </button>
                </div>
              )}
            </>
          )}
        </TableWrapper>
      </div>

      {/* Mobile View */}
      <div className="md:hidden space-y-6">
        <h3 className="text-base font-semibold text-gray-400">H2H Insights</h3>

        {/* HOME LAST MATCHES - Mobile */}
        <TableWrapper
          title={homeTeamName ? `${homeTeamName} — Last 10` : "Home — Last 10"}
          titleColor="text-green-400"
        >
          {!home.lastMatches || home.lastMatches.length === 0 ? (
            <div className="text-gray-500 p-4 text-center bg-gray-800/30 rounded-lg border border-gray-700/50">
              No matches available
            </div>
          ) : (
            renderMobileMatchCard(
              home.lastMatches.slice(0, 10),
              refHomeTeamName,
              true,
            )
          )}
        </TableWrapper>

        {/* AWAY LAST MATCHES - Mobile */}
        <TableWrapper
          title={awayTeamName ? `${awayTeamName} — Last 10` : "Away — Last 10"}
          titleColor="text-green-400"
        >
          {!away.lastMatches || away.lastMatches.length === 0 ? (
            <div className="text-gray-500 p-4 text-center bg-gray-800/30 rounded-lg border border-gray-700/50">
              No matches available
            </div>
          ) : (
            renderMobileMatchCard(
              away.lastMatches.slice(0, 10),
              refAwayTeamName,
              false,
            )
          )}
        </TableWrapper>

        {/* HEAD TO HEAD - Mobile */}
        <TableWrapper titleColor="text-green-400" title="H2H History">
          {renderMobileH2HCard()}
        </TableWrapper>
      </div>
    </div>
  );
}
